import { Component } from "@angular/core";
import { MatDialogRef } from "@angular/material";
import { FormBuilder, FormGroup, FormControl } from "@angular/forms";

import { SessionView } from "./models";
import { TranslateService } from "../common/translate";
import { SessionsService } from "./sessions.service";
import { SnackbarService } from "app/common/snackbar/snackbar.service";

@Component({
	selector: "sdp-sessions-disconnect-dialog",
	templateUrl: "./sessions-disconnect-dialog-component.html"
})
export class SessionsDisconnectDialogComponent {
	public readonly title = "sessions.disconnect.dialog.title";
	public readonly buttonCancel = "sdp.label.cancel";
	public readonly buttonAction = "sessions.disconnect.button.label";

	public message = "";

	public readonly form: FormGroup;

	private _session: SessionView | undefined;

	private submitted = false;

	constructor(
		private service: SessionsService,
		public dialogRef: MatDialogRef<SessionsDisconnectDialogComponent>,
		formBuilder: FormBuilder,
		private translateService: TranslateService,
		private snackbarService: SnackbarService
	) {
		this.form = formBuilder.group(
			{
				disable: [
					false
				],
				all: [
					false
				]
			}
		);
	}

	public get showProgressBar() {
		return this.submitted;
	}

	public get session() {
		if (!this._session) {
			throw new Error("expected a session");
		}
		return this._session;
	}

	public set session(value: SessionView) {
		this._session = value;

		if (!value.userType) {
			// TODO this case goes away once all session data must contain a userType
			throw new Error("No user type available, unable to disconnect sessions");
		} else if (value.userType === "cognito") {
			if (value.isPrimaryUser) {
				this.disableFormControl.setValue(false);
				this.disableFormControl.disable();
				this.allFormControl.setValue(true);
				this.allFormControl.enable();
			} else {
				this.disableFormControl.setValue(true);
				this.disableFormControl.enable();
				this.allFormControl.setValue(true);
				this.allFormControl.disable();
			}

			this.message = this.translateService.translate(
				"sessions.disconnect.dialog.message.cognito",
				{
					userName: value.userName
				}
			);
		} else if (value.userType === "saml") {
			this.disableFormControl.setValue(false);
			this.disableFormControl.disable();
			this.allFormControl.setValue(true);
			this.allFormControl.enable();

			this.message = this.translateService.translate(
				"sessions.disconnect.dialog.message.saml",
				{
					userName: value.userName
				}
			);
		} else {
			throw new Error(`unexpected user type for session: ${value.userType}`);
		}
	}

	public cancel() {
		this.dialogRef.close();
	}

	public async action() {
		this.submitted = true;
		try {
			if (this.disableFormControl.value) {
				await this.service.disableCognitoUser(this.session.userName);
				this.snackbarService.open(this.translateService.translate(
					"sessions.disconnect.success.disabled",
					{
						userName: this.session.userName
					}
				));
			} else if (this.allFormControl.value) {
				await this.service.disconnectSessions(this.session.userName, this.session.userType);
				this.snackbarService.open(this.translateService.translate(
					"sessions.disconnect.success.all",
					{
						userName: this.session.userName
					}
				));
			} else {
				await this.service.disconnectSession(this.session.sessionUuid);
				this.snackbarService.open(this.translateService.translate(
					"sessions.disconnect.success.single",
					{
						userName: this.session.userName
					}
				));
			}
		} catch (error) {
			this.snackbarService.open(this.translateService.translate(error));
		} finally {
			this.dialogRef.close();
		}
	}

	public get disableTooltip() {
		if (!this.disableFormControl.enabled) {
			if (this.session.userType === "saml") {
				return this.translateService.translate(
					"sessions.disconnect.tooltip.saml",
					{
						userName: this.session.userName
					}
				);
			} else {
				return "sessions.disconnect.tooltip.primary";
			}
		} else {
			// when the widget is enabled it needs no explanation
			return undefined;
		}
	}

	public disableChanged() {
		if (this.disableFormControl.value) {
			this.allFormControl.setValue(true);
			this.allFormControl.disable();
		} else {
			this.allFormControl.enable();
		}
	}

	public get allTooltip() {
		if (!this.allFormControl.enabled) {
			return "sessions.disconnect.tooltip.all";
		} else {
			// when the widget is enabled it needs no explanation
			return undefined;
		}
	}

	private get disableFormControl() {
		return this.form.controls["disable"] as FormControl;
	}

	private get allFormControl() {
		return this.form.controls["all"] as FormControl;
	}
}
